import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface JobViewCounts {
  [jobId: string]: { total: number; unique: number };
}

export function useJobViews(jobIds: string[]) {
  const jobKey = [...jobIds].sort().join(',');

  return useQuery({
    queryKey: ['job-views', jobKey],
    queryFn: async () => {
      if (!jobIds.length) return {} as JobViewCounts;

      const { data, error } = await supabase
        .from('job_views')
        .select('job_id, is_unique')
        .in('job_id', jobIds);

      if (error) throw error;

      const counts: JobViewCounts = {};
      jobIds.forEach(id => { counts[id] = { total: 0, unique: 0 }; });

      (data || []).forEach((row) => {
        if (!counts[row.job_id]) counts[row.job_id] = { total: 0, unique: 0 };
        counts[row.job_id].total++;
        if (row.is_unique) counts[row.job_id].unique++;
      });

      return counts;
    },
    enabled: jobIds.length > 0,
    staleTime: 30_000,
  });
}

export function useJobViewDetails(jobId: string | undefined) {
  return useQuery({
    queryKey: ['job-view-details', jobId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('job_views')
        .select('*')
        .eq('job_id', jobId!)
        .order('viewed_at', { ascending: false });

      if (error) throw error;

      const views = data || [];
      const totalViews = views.length;
      const uniqueViews = views.filter((view) => view.is_unique).length;

      // Device breakdown
      const devices: Record<string, number> = {};
      const browsers: Record<string, number> = {};
      const referrers: Record<string, number> = {};
      const countries: Record<string, number> = {};

      views.forEach((v) => {
        if (v.device_type) devices[v.device_type] = (devices[v.device_type] || 0) + 1;
        if (v.browser) browsers[v.browser] = (browsers[v.browser] || 0) + 1;
        if (v.referrer_domain) referrers[v.referrer_domain] = (referrers[v.referrer_domain] || 0) + 1;
        if (v.country) countries[v.country] = (countries[v.country] || 0) + 1;
      });

      return { totalViews, uniqueViews, devices, browsers, referrers, countries, views };
    },
    enabled: !!jobId,
    staleTime: 30_000,
  });
}
